import React, { useState, useEffect } from 'react';
import {
  CreditCard, Calendar, AlertTriangle, CheckCircle, Clock,
  Send, Upload, FileText, DollarSign, XCircle, Eye, Trash2, Ban, Wallet
} from 'lucide-react';
import { Card, Button, Input, Select, Textarea, Modal, Badge, ConfirmDialog, Loading } from '../components/ui';
import { recibosService, pagosPropietarioService, sancionesService, departamentosService } from '../services/api';
import { formatMoney, formatDate, getLocalDate } from '../utils/formatters';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const BANCOS = [
  { value: 'SALDO_FAVOR', label: '💰 Saldo a Favor', className: 'text-cyan-400 font-semibold' },
  { value: 'BCP', label: 'Banco de Crédito del Perú (BCP)' },
  { value: 'INTERBANK', label: 'Interbank' },
  { value: 'BBVA', label: 'BBVA Continental' },
  { value: 'SCOTIABANK', label: 'Scotiabank' },
  { value: 'BN', label: 'Banco de la Nación' },
  { value: 'YAPE', label: 'Yape' },
  { value: 'PLIN', label: 'Plin' },
  { value: 'OTRO', label: 'Otro' }
];

const MisPagosPage = () => {
  const { usuario } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    pendientes: [],
    pagados: [],
    mis_pagos: [],
    total_pendiente: 0,
    total_vencido: 0
  });
  const [sanciones, setSanciones] = useState([]);
  const [selectedRecibos, setSelectedRecibos] = useState([]);
  const [selectedSanciones, setSelectedSanciones] = useState([]);
  const [modalPago, setModalPago] = useState(false);
  const [modalAdelanto, setModalAdelanto] = useState(false);
  const [modalDetalle, setModalDetalle] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [selectedPago, setSelectedPago] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [formPago, setFormPago] = useState({
    numero_operacion: '',
    banco: '',
    fecha_pago: getLocalDate(),
    observaciones: ''
  });

  const [formAdelanto, setFormAdelanto] = useState({
    monto_adelanto: '',
    numero_operacion: '',
    banco: '',
    fecha_pago: getLocalDate(),
    observaciones: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [recibosRes, sancionesRes] = await Promise.all([
        recibosService.getMiCuenta(),
        sancionesService.getByDepartamento(usuario.departamento_id)
      ]);
      setData(recibosRes.data.data);

      // Debug: ver qué datos tienen los pagos
      console.log('Mis pagos:', recibosRes.data.data.mis_pagos);

      // Filtrar solo sanciones pendientes
      const sancionesPendientes = (sancionesRes.data.data?.sanciones || [])
        .filter(s => s.estado === 'PENDIENTE');
      setSanciones(sancionesPendientes);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectRecibo = (reciboId) => {
    setSelectedRecibos(prev =>
      prev.includes(reciboId)
        ? prev.filter(id => id !== reciboId)
        : [...prev, reciboId]
    );
  };

  const handleSelectSancion = (sancionId) => {
    setSelectedSanciones(prev =>
      prev.includes(sancionId)
        ? prev.filter(id => id !== sancionId)
        : [...prev, sancionId]
    );
  };

  const handleSelectAll = () => {
    if (selectedRecibos.length === data.pendientes.length) {
      setSelectedRecibos([]);
    } else {
      setSelectedRecibos(data.pendientes.map(r => r.id));
    }
  };

  const handleSelectAllSanciones = () => {
    if (selectedSanciones.length === sanciones.length) {
      setSelectedSanciones([]);
    } else {
      setSelectedSanciones(sanciones.map(s => s.id));
    }
  };

  const getMontoSeleccionado = () => {
    const montoRecibos = data.pendientes
      .filter(r => selectedRecibos.includes(r.id))
      .reduce((sum, r) => sum + parseFloat(r.monto), 0);
    const montoSanciones = sanciones
      .filter(s => selectedSanciones.includes(s.id))
      .reduce((sum, s) => sum + parseFloat(s.monto), 0);
    return montoRecibos + montoSanciones;
  };

  const getTotalSancionesPendientes = () => {
    return sanciones.reduce((sum, s) => sum + parseFloat(s.monto), 0);
  };

  const handleSubmitPago = async (e) => {
    e.preventDefault();

    if (selectedRecibos.length === 0 && selectedSanciones.length === 0) {
      toast.error('Selecciona al menos un recibo o sanción');
      return;
    }

    // Validar saldo si se seleccionó pago con saldo a favor
    if (formPago.banco === 'SALDO_FAVOR') {
      const montoTotal = getMontoSeleccionado();
      const saldoDisponible = data.saldo_favor || 0;

      if (saldoDisponible < montoTotal) {
        toast.error(
          `Saldo insuficiente. Disponible: ${formatMoney(saldoDisponible)}, Requerido: ${formatMoney(montoTotal)}`
        );
        return;
      }
    }

    setSubmitting(true);
    try {
      // Si es pago con saldo a favor, usar endpoint diferente
      if (formPago.banco === 'SALDO_FAVOR') {
        await departamentosService.pagarConSaldo(usuario.departamento_id, {
          recibos: selectedRecibos,
          sanciones: selectedSanciones
        });
        toast.success('¡Pago realizado con éxito! Se descontó de tu saldo a favor.');
      } else {
        // Pago bancario normal
        await pagosPropietarioService.create({
          recibos: selectedRecibos,
          sanciones: selectedSanciones,
          ...formPago
        });
        toast.success('¡Pago registrado! Pendiente de confirmación por tesorería.');
      }

      setModalPago(false);
      setSelectedRecibos([]);
      setSelectedSanciones([]);
      setFormPago({
        numero_operacion: '',
        banco: '',
        fecha_pago: getLocalDate(),
        observaciones: ''
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al registrar pago');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitAdelanto = async (e) => {
    e.preventDefault();

    if (!formAdelanto.monto_adelanto || parseFloat(formAdelanto.monto_adelanto) <= 0) {
      toast.error('Ingresa un monto válido');
      return;
    }

    setSubmitting(true);
    try {
      await pagosPropietarioService.create({
        es_adelanto: true,
        monto_adelanto: parseFloat(formAdelanto.monto_adelanto),
        numero_operacion: formAdelanto.numero_operacion,
        banco: formAdelanto.banco,
        fecha_pago: formAdelanto.fecha_pago,
        observaciones: formAdelanto.observaciones
      });

      toast.success('¡Adelanto registrado! Pendiente de confirmación por tesorería.');
      setModalAdelanto(false);
      setFormAdelanto({
        monto_adelanto: '',
        numero_operacion: '',
        banco: '',
        fecha_pago: getLocalDate(),
        observaciones: ''
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al registrar adelanto');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePago = async () => {
    try {
      await pagosPropietarioService.delete(selectedPago.id);
      toast.success('Pago eliminado');
      setConfirmDelete(false);
      setSelectedPago(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al eliminar');
    }
  };

  const getEstadoPagoVariant = (estado) => {
    const variants = { PENDIENTE: 'warning', CONFIRMADO: 'success', RECHAZADO: 'danger' };
    return variants[estado] || 'default';
  };

  if (loading) return <Loading text="Cargando tu cuenta..." />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Mis Pagos</h1>
          <p className="text-slate-400">Registra y gestiona tus pagos de mantenimiento</p>
        </div>
        <Button
          variant="secondary"
          icon={Wallet}
          onClick={() => setModalAdelanto(true)}
        >
          Registrar Adelanto
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="p-5">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-amber-500/20">
              <DollarSign className="text-amber-400" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-400">Total Pendiente</p>
              <p className="text-2xl font-bold text-amber-400">
                {formatMoney((data.total_pendiente || 0) + getTotalSancionesPendientes())}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-rose-500/20">
              <AlertTriangle className="text-rose-400" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-400">Vencido</p>
              <p className="text-2xl font-bold text-rose-400">{formatMoney(data.total_vencido || 0)}</p>
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-blue-500/20">
              <FileText className="text-blue-400" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-400">Recibos</p>
              <p className="text-2xl font-bold text-white">{data.pendientes?.length || 0}</p>
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-rose-500/20">
              <Ban className="text-rose-400" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-400">Sanciones</p>
              <p className="text-2xl font-bold text-rose-400">{sanciones.length}</p>
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-cyan-500/20">
              <Wallet className="text-cyan-400" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-400">Saldo a Favor</p>
              <p className="text-2xl font-bold text-cyan-400">{formatMoney(data.saldo_favor || 0)}</p>
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-emerald-500/20">
              <CheckCircle className="text-emerald-400" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-400">Seleccionado</p>
              <p className="text-2xl font-bold text-emerald-400">{formatMoney(getMontoSeleccionado())}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Recibos Pendientes */}
      <Card>
        <div className="p-4 border-b border-slate-700 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-white">Recibos por Pagar</h3>
            <p className="text-sm text-slate-400">Selecciona los recibos que deseas pagar</p>
          </div>
          {data.pendientes.length > 0 && (
            <div className="flex items-center gap-3">
              <button
                onClick={handleSelectAll}
                className="text-sm text-blue-400 hover:text-blue-300"
              >
                {selectedRecibos.length === data.pendientes.length ? 'Deseleccionar todo' : 'Seleccionar todo'}
              </button>
              {selectedRecibos.length > 0 && (
                <Button onClick={() => setModalPago(true)} icon={Send}>
                  Registrar Pago ({selectedRecibos.length})
                </Button>
              )}
            </div>
          )}
        </div>

        {data.pendientes.length === 0 ? (
          <div className="p-8 text-center">
            <CheckCircle className="mx-auto text-emerald-400 mb-3" size={48} />
            <p className="text-slate-400">¡Estás al día! No tienes recibos pendientes.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-700">
            {data.pendientes.map((recibo) => (
              <div
                key={recibo.id}
                className={`p-4 flex items-center gap-4 hover:bg-slate-700/30 cursor-pointer transition-colors ${selectedRecibos.includes(recibo.id) ? 'bg-blue-500/10' : ''
                  }`}
                onClick={() => handleSelectRecibo(recibo.id)}
              >
                <input
                  type="checkbox"
                  checked={selectedRecibos.includes(recibo.id)}
                  onChange={() => { }}
                  className="w-5 h-5 rounded border-slate-600 bg-slate-700 text-blue-500 focus:ring-blue-500"
                />

                <div className="flex-1 min-w-0">
                  <p className="font-medium text-white">{recibo.concepto}</p>
                  <div className="flex items-center gap-3 text-sm text-slate-400 mt-1">
                    <span className="flex items-center gap-1">
                      <Calendar size={14} />
                      Vence: {formatDate(recibo.fecha_vencimiento)}
                    </span>
                    {recibo.tipo !== 'CUOTA_MANTENIMIENTO' && (
                      <Badge variant="purple" size="sm">{recibo.tipo}</Badge>
                    )}
                  </div>
                </div>

                <div className="text-right">
                  <p className={`text-lg font-bold ${recibo.es_vencido ? 'text-rose-400' : 'text-white'}`}>
                    {formatMoney(recibo.monto)}
                  </p>
                  {recibo.es_vencido ? (
                    <Badge variant="danger" size="sm">Vencido</Badge>
                  ) : (
                    <Badge variant="warning" size="sm">Pendiente</Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Sanciones Pendientes */}
      {sanciones.length > 0 && (
        <Card>
          <div className="p-4 border-b border-slate-700 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-white flex items-center gap-2">
                <Ban className="text-rose-400" size={20} />
                Sanciones por Pagar
              </h3>
              <p className="text-sm text-slate-400">Total: {formatMoney(getTotalSancionesPendientes())}</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleSelectAllSanciones}
                className="text-sm text-blue-400 hover:text-blue-300"
              >
                {selectedSanciones.length === sanciones.length ? 'Deseleccionar todo' : 'Seleccionar todo'}
              </button>
            </div>
          </div>

          <div className="divide-y divide-slate-700">
            {sanciones.map((sancion) => (
              <div
                key={sancion.id}
                className={`p-4 flex items-center gap-4 hover:bg-slate-700/30 cursor-pointer transition-colors ${selectedSanciones.includes(sancion.id) ? 'bg-rose-500/10' : ''
                  }`}
                onClick={() => handleSelectSancion(sancion.id)}
              >
                <input
                  type="checkbox"
                  checked={selectedSanciones.includes(sancion.id)}
                  onChange={() => { }}
                  className="w-5 h-5 rounded border-slate-600 bg-slate-700 text-rose-500 focus:ring-rose-500"
                />

                <div className="flex-1 min-w-0">
                  <p className="font-medium text-white">{sancion.descripcion}</p>
                  <div className="flex items-center gap-3 text-sm text-slate-400 mt-1">
                    <span className="flex items-center gap-1">
                      <Calendar size={14} />
                      {formatDate(sancion.fecha_infraccion)}
                    </span>
                    <Badge variant={
                      sancion.tipo === 'LEVE' ? 'warning' :
                        sancion.tipo === 'GRAVE' ? 'danger' : 'danger'
                    } size="sm">
                      {sancion.tipo}
                    </Badge>
                  </div>
                </div>

                <div className="text-right">
                  <p className="text-lg font-bold text-rose-400">
                    {formatMoney(sancion.monto)}
                  </p>
                  <Badge variant="danger" size="sm">Sanción</Badge>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Botón flotante para registrar pago si hay selección */}
      {(selectedRecibos.length > 0 || selectedSanciones.length > 0) && (
        <div className="fixed bottom-6 right-6 z-50">
          <Button
            onClick={() => setModalPago(true)}
            icon={Send}
            className="shadow-lg shadow-emerald-500/30"
          >
            Registrar Pago ({formatMoney(getMontoSeleccionado())})
          </Button>
        </div>
      )}

      {/* Mis Pagos Registrados */}
      <Card>
        <div className="p-4 border-b border-slate-700">
          <h3 className="font-semibold text-white">Mis Pagos Registrados</h3>
          <p className="text-sm text-slate-400">Historial de pagos enviados</p>
        </div>

        {data.mis_pagos.length === 0 ? (
          <div className="p-8 text-center text-slate-400">
            No has registrado pagos aún
          </div>
        ) : (
          <div className="divide-y divide-slate-700">
            {data.mis_pagos.map((pago) => (
              <div key={pago.id} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${pago.es_adelanto ? 'bg-cyan-500/20' :
                      pago.estado === 'CONFIRMADO' ? 'bg-emerald-500/20' :
                        pago.estado === 'RECHAZADO' ? 'bg-rose-500/20' : 'bg-amber-500/20'
                      }`}>
                      {pago.es_adelanto ? <Wallet className="text-cyan-400" size={20} /> :
                        pago.estado === 'CONFIRMADO' ? <CheckCircle className="text-emerald-400" size={20} /> :
                          pago.estado === 'RECHAZADO' ? <XCircle className="text-rose-400" size={20} /> :
                            <Clock className="text-amber-400" size={20} />}
                    </div>
                    <div>
                      <p className="font-medium text-white flex items-center gap-2">
                        Op: {pago.numero_operacion} - {pago.banco || 'Sin banco'}
                        {pago.es_adelanto && (
                          <Badge variant="info" size="sm">Adelanto</Badge>
                        )}
                      </p>
                      <p className="text-sm text-slate-400">
                        {formatDate(pago.fecha_pago)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="font-bold text-white">{formatMoney(pago.monto_total)}</p>
                      <Badge variant={getEstadoPagoVariant(pago.estado)} size="sm">
                        {pago.estado}
                      </Badge>
                    </div>

                    <div className="flex gap-1">
                      <button
                        onClick={() => { setSelectedPago(pago); setModalDetalle(true); }}
                        className="p-2 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-white"
                      >
                        <Eye size={16} />
                      </button>
                      {pago.estado === 'PENDIENTE' && (
                        <button
                          onClick={() => { setSelectedPago(pago); setConfirmDelete(true); }}
                          className="p-2 rounded-lg hover:bg-slate-700 text-slate-400 hover:text-rose-400"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Detalle de recibos o indicar adelanto */}
                {pago.es_adelanto ? (
                  <div className="mt-2 ml-14 text-sm text-cyan-400">
                    Pago adelantado → Saldo a favor
                  </div>
                ) : pago.detalle_recibos && (
                  <div className="mt-2 ml-14 text-sm text-slate-500">
                    <span className="text-slate-400">Incluye: </span>
                    {pago.detalle_recibos}
                  </div>
                )}

                {pago.estado === 'RECHAZADO' && pago.motivo_rechazo && (
                  <p className="mt-2 ml-14 text-sm text-rose-400">Motivo: {pago.motivo_rechazo}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Historial de Pagados */}
      <Card>
        <div className="p-4 border-b border-slate-700">
          <h3 className="font-semibold text-white">Historial de Recibos Pagados</h3>
        </div>

        {data.pagados.length === 0 ? (
          <div className="p-8 text-center text-slate-400">
            No hay recibos pagados
          </div>
        ) : (
          <div className="divide-y divide-slate-700">
            {data.pagados.map((recibo) => (
              <div key={recibo.id} className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle className="text-emerald-400" size={20} />
                  <div>
                    <p className="text-white">{recibo.concepto}</p>
                    <p className="text-sm text-slate-400">{recibo.mes}/{recibo.anio}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium text-emerald-400">{formatMoney(recibo.monto)}</p>
                  <Badge variant="success" size="sm">Pagado</Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Historial de Sanciones Pagadas */}
      <Card>
        <div className="p-4 border-b border-slate-700">
          <h3 className="font-semibold text-white">Historial de Sanciones Pagadas</h3>
        </div>

        {!data.sanciones_pagadas || data.sanciones_pagadas.length === 0 ? (
          <div className="p-8 text-center text-slate-400">
            No hay sanciones pagadas
          </div>
        ) : (
          <div className="divide-y divide-slate-700">
            {data.sanciones_pagadas.map((sancion) => (
              <div key={sancion.id} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="text-emerald-400" size={20} />
                    <div>
                      <p className="text-white font-medium">{sancion.descripcion}</p>
                      <div className="flex items-center gap-3 text-sm text-slate-400 mt-1">
                        <span>Sancionado: {formatDate(sancion.fecha_infraccion)}</span>
                        {sancion.tipo && (
                          <Badge variant="danger" size="sm">{sancion.tipo}</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-emerald-400">{formatMoney(sancion.monto)}</p>
                    <Badge variant="success" size="sm">Pagada</Badge>
                  </div>
                </div>
                <div className="mt-2 ml-9 text-sm text-slate-400">
                  <p>
                    <span className="text-slate-500">Pagado:</span> {formatDate(sancion.fecha_pago)}
                    {sancion.metodo_pago && (
                      <span className="ml-3">
                        <span className="text-slate-500">Método:</span> {sancion.metodo_pago}
                      </span>
                    )}
                  </p>
                  {sancion.referencia_pago && (
                    <p className="text-slate-500">
                      Ref: <span className="text-slate-400 font-mono">{sancion.referencia_pago}</span>
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Información de Pago */}
      <Card className="p-5">
        <h3 className="font-semibold text-white mb-4">Información para Pagos</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-slate-400">Banco:</span>
              <span className="text-white font-medium">BCP / Interbank</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">N° Cuenta BCP:</span>
              <span className="text-white font-mono">123-456789-0-12</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">N° Cuenta Interbank:</span>
              <span className="text-white font-mono">789-123456789-01</span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-slate-400">CCI BCP:</span>
              <span className="text-white font-mono text-xs">002-123-456789012345-12</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Titular:</span>
              <span className="text-white">Junta de Propietarios</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Yape/Plin:</span>
              <span className="text-white font-mono">999-888-777</span>
            </div>
          </div>
        </div>
        <p className="text-amber-400 text-sm mt-4">
          * Recuerda guardar el número de operación para registrar tu pago
        </p>
      </Card>

      {/* Modal Registrar Pago */}
      <Modal
        isOpen={modalPago}
        onClose={() => setModalPago(false)}
        title="Registrar Pago"
        size="lg"
      >
        <form onSubmit={handleSubmitPago} className="space-y-4">
          {/* Resumen de recibos y sanciones seleccionados */}
          <div className="p-4 rounded-xl bg-slate-700/50 max-h-48 overflow-y-auto">
            {selectedRecibos.length > 0 && (
              <>
                <p className="text-sm text-slate-400 mb-2">Recibos seleccionados:</p>
                <div className="space-y-1 mb-3">
                  {data.pendientes
                    .filter(r => selectedRecibos.includes(r.id))
                    .map(r => (
                      <div key={r.id} className="flex justify-between text-sm">
                        <span className="text-slate-300">{r.concepto}</span>
                        <span className="text-white font-medium">{formatMoney(r.monto)}</span>
                      </div>
                    ))
                  }
                </div>
              </>
            )}

            {selectedSanciones.length > 0 && (
              <>
                <p className="text-sm text-rose-400 mb-2">Sanciones seleccionadas:</p>
                <div className="space-y-1 mb-3">
                  {sanciones
                    .filter(s => selectedSanciones.includes(s.id))
                    .map(s => (
                      <div key={s.id} className="flex justify-between text-sm">
                        <span className="text-slate-300">{s.descripcion}</span>
                        <span className="text-rose-400 font-medium">{formatMoney(s.monto)}</span>
                      </div>
                    ))
                  }
                </div>
              </>
            )}

            <div className="border-t border-slate-600 mt-3 pt-3 flex justify-between">
              <span className="font-semibold text-white">Total a pagar:</span>
              <span className="text-xl font-bold text-emerald-400">{formatMoney(getMontoSeleccionado())}</span>
            </div>
          </div>

          {/* Mostrar saldo disponible siempre */}
          <div className="p-4 rounded-xl bg-cyan-500/10 border border-cyan-500/30">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-cyan-300">Tu saldo a favor disponible:</p>
                <p className="text-2xl font-bold text-cyan-400">{formatMoney(data.saldo_favor || 0)}</p>
              </div>
              <Wallet className="text-cyan-400" size={32} />
            </div>
            {(data.saldo_favor || 0) >= getMontoSeleccionado() && getMontoSeleccionado() > 0 && (
              <p className="text-xs text-emerald-400 mt-2 flex items-center gap-1">
                <CheckCircle size={14} />
                Puedes pagar con tu saldo a favor
              </p>
            )}
            {(data.saldo_favor || 0) < getMontoSeleccionado() && getMontoSeleccionado() > 0 && (
              <p className="text-xs text-amber-400 mt-2 flex items-center gap-1">
                <AlertTriangle size={14} />
                Saldo insuficiente para pagar con saldo a favor
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {formPago.banco !== 'SALDO_FAVOR' && (
              <Input
                label="Número de Operación *"
                value={formPago.numero_operacion}
                onChange={(e) => setFormPago({ ...formPago, numero_operacion: e.target.value })}
                placeholder="Ej: 12345678"
                required
              />
            )}
            <Select
              label="Banco / Medio de Pago"
              value={formPago.banco}
              onChange={(e) => setFormPago({ ...formPago, banco: e.target.value })}
              options={BANCOS}
              placeholder="Seleccionar..."
              className={formPago.banco === 'SALDO_FAVOR' ? 'md:col-span-2' : ''}
            />
          </div>

          {/* Mostrar saldo disponible si selecciona Saldo a Favor */}
          {formPago.banco === 'SALDO_FAVOR' && (
            <div className="p-4 rounded-xl bg-cyan-500/10 border border-cyan-500/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-cyan-300">Saldo disponible:</p>
                  <p className="text-2xl font-bold text-cyan-400">{formatMoney(data.saldo_favor || 0)}</p>
                </div>
                <Wallet className="text-cyan-400" size={32} />
              </div>
              <p className="text-xs text-slate-400 mt-2">
                El pago se procesará inmediatamente y se descontará de tu saldo a favor.
              </p>
            </div>
          )}

          {formPago.banco !== 'SALDO_FAVOR' && (
            <Input
              label="Fecha de Pago *"
              type="date"
              value={formPago.fecha_pago}
              onChange={(e) => setFormPago({ ...formPago, fecha_pago: e.target.value })}
              required
            />
          )}

          <Textarea
            label="Observaciones"
            value={formPago.observaciones}
            onChange={(e) => setFormPago({ ...formPago, observaciones: e.target.value })}
            placeholder="Información adicional sobre el pago..."
            rows={2}
          />

          <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
            <p className="text-sm text-blue-300">
              <strong>Nota:</strong> Tu pago será revisado por tesorería y confirmado en las próximas 24-48 horas.
              Recibirás una notificación cuando sea aprobado.
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setModalPago(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={submitting} icon={Send}>
              {submitting ? 'Enviando...' : 'Enviar Pago'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal Registrar Adelanto */}
      <Modal
        isOpen={modalAdelanto}
        onClose={() => setModalAdelanto(false)}
        title="Registrar Pago Adelantado"
      >
        <form onSubmit={handleSubmitAdelanto} className="space-y-4">
          <div className="p-4 rounded-xl bg-cyan-500/10 border border-cyan-500/30 mb-4">
            <p className="text-sm text-cyan-300">
              <strong>¿Qué es un pago adelantado?</strong><br />
              Es un depósito que realizas sin asociar a recibos específicos.
              El monto quedará como saldo a favor para descontar de tus próximas cuotas.
            </p>
          </div>

          <Input
            label="Monto del adelanto"
            type="number"
            step="0.01"
            min="0.01"
            value={formAdelanto.monto_adelanto}
            onChange={(e) => setFormAdelanto({ ...formAdelanto, monto_adelanto: e.target.value })}
            placeholder="Ej: 500.00"
            required
          />

          <Input
            label="Número de operación / Voucher"
            value={formAdelanto.numero_operacion}
            onChange={(e) => setFormAdelanto({ ...formAdelanto, numero_operacion: e.target.value })}
            placeholder="Ej: 123456789"
            required
          />

          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Banco"
              value={formAdelanto.banco}
              onChange={(e) => setFormAdelanto({ ...formAdelanto, banco: e.target.value })}
              options={BANCOS}
              required
            />
            <Input
              label="Fecha de depósito"
              type="date"
              value={formAdelanto.fecha_pago}
              onChange={(e) => setFormAdelanto({ ...formAdelanto, fecha_pago: e.target.value })}
              required
            />
          </div>

          <Textarea
            label="Observaciones (opcional)"
            value={formAdelanto.observaciones}
            onChange={(e) => setFormAdelanto({ ...formAdelanto, observaciones: e.target.value })}
            placeholder="Ej: Adelanto para cuotas de los próximos meses"
            rows={2}
          />

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setModalAdelanto(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={submitting} icon={Wallet}>
              {submitting ? 'Enviando...' : 'Registrar Adelanto'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal Detalle Pago */}
      <Modal
        isOpen={modalDetalle}
        onClose={() => setModalDetalle(false)}
        title={selectedPago?.es_adelanto ? "Detalle del Adelanto" : "Detalle del Pago"}
      >
        {selectedPago && (
          <div className="space-y-4">
            <div className={`p-4 rounded-xl ${selectedPago.es_adelanto ? 'bg-cyan-500/10' : 'bg-slate-700/50'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Badge variant={getEstadoPagoVariant(selectedPago.estado)} size="lg">
                    {selectedPago.estado}
                  </Badge>
                  {selectedPago.es_adelanto && (
                    <Badge variant="info" size="lg">ADELANTO</Badge>
                  )}
                </div>
                <span className="text-2xl font-bold text-white">
                  {formatMoney(selectedPago.monto_total)}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-500">N° Operación</p>
                <p className="text-white font-mono">{selectedPago.numero_operacion}</p>
              </div>
              <div>
                <p className="text-slate-500">Banco</p>
                <p className="text-white">{selectedPago.banco || '-'}</p>
              </div>
              <div>
                <p className="text-slate-500">Fecha de Pago</p>
                <p className="text-white">{formatDate(selectedPago.fecha_pago)}</p>
              </div>
              <div>
                <p className="text-slate-500">Registrado</p>
                <p className="text-white">{formatDate(selectedPago.created_at)}</p>
              </div>
            </div>

            {/* Si es adelanto, mostrar info especial */}
            {selectedPago.es_adelanto ? (
              <div className="p-4 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
                <div className="flex items-center gap-2 mb-2">
                  <Wallet size={20} className="text-cyan-400" />
                  <p className="text-cyan-300 font-medium">Pago Adelantado</p>
                </div>
                <p className="text-sm text-slate-400">
                  Este monto {selectedPago.estado === 'CONFIRMADO' ? 'fue agregado' : 'será agregado'} a tu saldo a favor
                  para descontar de tus próximas cuotas.
                </p>
              </div>
            ) : (
              <>
                {/* Detalle de recibos incluidos */}
                {selectedPago.recibos_detalle && selectedPago.recibos_detalle.length > 0 && (
                  <div>
                    <p className="text-slate-500 text-sm mb-2">Recibos incluidos ({selectedPago.recibos_detalle.length}):</p>
                    <div className="rounded-lg bg-slate-700/30 divide-y divide-slate-600">
                      {selectedPago.recibos_detalle.map((recibo, idx) => (
                        <div key={idx} className="p-3 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <FileText size={16} className="text-blue-400" />
                            <div>
                              <p className="text-white text-sm">{recibo.concepto}</p>
                              <p className="text-slate-500 text-xs">{recibo.mes}/{recibo.anio}</p>
                            </div>
                          </div>
                          <span className="text-emerald-400 font-medium">{formatMoney(recibo.monto)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}


                {/* Sanciones incluidas */}
                {selectedPago.sanciones && selectedPago.sanciones.length > 0 && (
                  <div>
                    <p className="text-slate-500 text-sm mb-2">Sanciones incluidas ({selectedPago.sanciones.length}):</p>
                    <div className="rounded-lg bg-rose-500/10 border border-rose-500/20 divide-y divide-rose-500/20">
                      {selectedPago.sanciones.map((sancion, idx) => (
                        <div key={idx} className="p-3 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Ban size={16} className="text-rose-400" />
                            <div>
                              <p className="text-white text-sm">{sancion.descripcion || sancion.concepto}</p>
                              <p className="text-slate-500 text-xs">{formatDate(sancion.fecha_infraccion)}</p>
                            </div>
                          </div>
                          <span className="text-rose-400 font-medium">{formatMoney(sancion.monto)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {selectedPago.observaciones && (
              <div>
                <p className="text-slate-500 text-sm">Observaciones</p>
                <p className="text-slate-300">{selectedPago.observaciones}</p>
              </div>
            )}

            {selectedPago.estado === 'CONFIRMADO' && selectedPago.fecha_confirmacion && (
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <p className="text-sm text-emerald-300">
                  ✓ Confirmado el {formatDate(selectedPago.fecha_confirmacion)}
                </p>
              </div>
            )}

            {selectedPago.estado === 'RECHAZADO' && selectedPago.motivo_rechazo && (
              <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/30">
                <p className="text-sm text-rose-300">
                  Motivo del rechazo: {selectedPago.motivo_rechazo}
                </p>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Confirm Delete */}
      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDeletePago}
        title="Eliminar Pago"
        message={`¿Eliminar el pago con operación ${selectedPago?.numero_operacion}? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
      />
    </div>
  );
};

export default MisPagosPage;
